import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';
import { getAuthHeaders } from '../../config/api';
import styles from './ContactMessages.module.scss';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Email templates for quick replies
  const emailTemplates = {
    greeting: `Dear [NAME],

Thank you for contacting Hotel Palais Royal. We have received your inquiry regarding "[SUBJECT]".

`,
    availability: `We are pleased to inform you that we have availability for your requested dates. Our rooms range from comfortable standard rooms to luxurious suites, each equipped with modern amenities.

Would you like us to send you detailed information about our rooms and rates?`,
    
    booking: `To proceed with your reservation, please provide us with the following information:
- Check-in date
- Check-out date
- Number of guests
- Room preference (Standard Single/Double, Superior Single/Double, Triple, or Suite)

We will then send you a confirmation with all the details.`,
    
    general: `We appreciate your interest in Hotel Palais Royal. Our team is here to assist you with any questions or special requests you may have.

Please feel free to contact us directly at +216 70 146 146 or reply to this email.`,
    
    closing: `

Best regards,
Hotel Palais Royal Team

Route Mahdia (Avenue Habib Bouguatfa)
Sfax 3000, Tunisia
Phone: +216 70 146 146
Email: reservation@palaisroyal.com.tn`
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.data || []);
      } else {
        showAlert('error', 'Failed to fetch messages');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      showAlert('error', 'Error loading messages');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setViewDialogOpen(true);

    // Mark as read
    if (message.status === 'new') {
      try {
        await fetch(`${API_BASE_URL}/api/contact/${message._id}`, {
          headers: getAuthHeaders()
        });
        fetchMessages();
      } catch (error) {
        console.error('Mark as read error:', error);
      }
    }
  };

  const handleOpenReply = (message) => {
    setSelectedMessage(message);
    const greeting = emailTemplates.greeting
      .replace('[NAME]', message.name)
      .replace('[SUBJECT]', message.subject);
    setReplyMessage(greeting + emailTemplates.general + emailTemplates.closing);
    setReplyDialogOpen(true);
  };

  const handleInsertTemplate = (template) => {
    const templateText = emailTemplates[template];
    const greeting = emailTemplates.greeting
      .replace('[NAME]', selectedMessage.name)
      .replace('[SUBJECT]', selectedMessage.subject);
    
    let fullMessage = greeting;
    if (template !== 'greeting' && template !== 'closing') {
      fullMessage += templateText;
    }
    fullMessage += emailTemplates.closing;
    
    setReplyMessage(fullMessage);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      showAlert('error', 'Please enter a reply message');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact/${selectedMessage._id}/reply`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyMessage })
      });

      if (response.ok) {
        showAlert('success', 'Reply sent successfully!');
        setReplyDialogOpen(false);
        setReplyMessage('');
        fetchMessages();
      } else {
        showAlert('error', 'Failed to send reply');
      }
    } catch (error) {
      console.error('Send reply error:', error);
      showAlert('error', 'Error sending reply');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showAlert('success', 'Message deleted');
        fetchMessages();
      } else {
        showAlert('error', 'Failed to delete message');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showAlert('error', 'Error deleting message');
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'new': return styles.statusNew;
      case 'read': return styles.statusRead;
      case 'replied': return styles.statusReplied;
      default: return '';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Contact Messages</h2>
      </div>

      {alert.show && (
        <div className={`${styles.alert} ${styles[`alert${alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}`]}`}>
          {alert.message}
          <button onClick={() => setAlert({ show: false })} className={styles.alertClose}>×</button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Messages</div>
          <div className={styles.statValue}>{messages.length}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>New Messages</div>
          <div className={`${styles.statValue} ${styles.statValueNew}`}>
            {messages.filter(m => m.status === 'new').length}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Replied</div>
          <div className={`${styles.statValue} ${styles.statValueReplied}`}>
            {messages.filter(m => m.status === 'replied').length}
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Date</th>
              <th className={styles.actionsCol}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 ? (
              <tr>
                <td colSpan="6" className={styles.emptyCell}>
                  No messages yet
                </td>
              </tr>
            ) : (
              messages.map((message) => (
                <tr 
                  key={message._id}
                  className={message.status === 'new' ? styles.rowNew : ''}
                >
                  <td>
                    <span className={`${styles.statusBadge} ${getStatusClass(message.status)}`}>
                      {message.status.toUpperCase()}
                    </span>
                  </td>
                  <td>{message.name}</td>
                  <td>{message.email}</td>
                  <td>{message.subject}</td>
                  <td>{formatDate(message.createdAt)}</td>
                  <td className={styles.actionsCol}>
                    <button 
                      className={styles.actionBtn}
                      onClick={() => handleViewMessage(message)}
                      title="View"
                    >
                      👁️
                    </button>
                    <button 
                      className={styles.actionBtn}
                      onClick={() => handleOpenReply(message)}
                      title="Reply"
                    >
                      ✉️
                    </button>
                    <button 
                      className={`${styles.actionBtn} ${styles.actionBtnDelete}`}
                      onClick={() => handleDeleteMessage(message._id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Message Dialog */}
      {viewDialogOpen && selectedMessage && (
        <div className={styles.modalOverlay} onClick={() => setViewDialogOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Message Details</h3>
              <span className={`${styles.statusBadge} ${getStatusClass(selectedMessage.status)}`}>
                {selectedMessage.status.toUpperCase()}
              </span>
              <button className={styles.modalClose} onClick={() => setViewDialogOpen(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <strong>Name:</strong> {selectedMessage.name}
                </div>
                <div className={styles.infoItem}>
                  <strong>Email:</strong> {selectedMessage.email}
                </div>
                <div className={styles.infoItem}>
                  <strong>Phone:</strong> {selectedMessage.phone || 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>Date:</strong> {formatDate(selectedMessage.createdAt)}
                </div>
              </div>

              <div className={styles.messageSection}>
                <h4>Subject</h4>
                <p>{selectedMessage.subject}</p>
              </div>

              <div className={styles.messageSection}>
                <h4>Message</h4>
                <div className={styles.messageBox}>
                  {selectedMessage.message}
                </div>
              </div>

              {selectedMessage.reply && (
                <div className={styles.messageSection}>
                  <h4 className={styles.replyTitle}>Reply Sent</h4>
                  <div className={styles.replyBox}>
                    {selectedMessage.reply.message}
                    <div className={styles.replyMeta}>
                      Sent on {formatDate(selectedMessage.reply.sentAt)} by {selectedMessage.reply.sentBy}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setViewDialogOpen(false)}>
                Close
              </button>
              {selectedMessage.status !== 'replied' && (
                <button 
                  className={styles.btnPrimary}
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenReply(selectedMessage);
                  }}
                >
                  Reply
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reply Dialog */}
      {replyDialogOpen && selectedMessage && (
        <div className={styles.modalOverlay} onClick={() => setReplyDialogOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Reply to {selectedMessage.name}</h3>
              <button className={styles.modalClose} onClick={() => setReplyDialogOpen(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.replyInfo}>
                <strong>To:</strong> {selectedMessage.email}<br />
                <strong>Subject:</strong> Re: {selectedMessage.subject}
              </div>

              <div className={styles.templatesSection}>
                <label>Quick Reply Templates:</label>
                <div className={styles.templateButtons}>
                  <button 
                    className={styles.templateBtn}
                    onClick={() => handleInsertTemplate('availability')}
                  >
                    Availability Info
                  </button>
                  <button 
                    className={styles.templateBtn}
                    onClick={() => handleInsertTemplate('booking')}
                  >
                    Booking Request
                  </button>
                  <button 
                    className={styles.templateBtn}
                    onClick={() => handleInsertTemplate('general')}
                  >
                    General Reply
                  </button>
                </div>
              </div>

              <textarea
                className={styles.replyTextarea}
                rows={12}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your reply here..."
              />

              <div className={styles.tipText}>
                Tip: Use the templates above to quickly insert common responses
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnSecondary}
                onClick={() => setReplyDialogOpen(false)}
                disabled={sending}
              >
                Cancel
              </button>
              <button 
                className={styles.btnPrimary}
                onClick={handleSendReply}
                disabled={sending || !replyMessage.trim()}
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
